import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { saveManualNutritionGoals } from "@/lib/nutritionGoalsExecutor";
import { useXP } from "@/hooks/useXP";
import { useNutritionResource, notifyNutritionChanged } from "@/hooks/useNutritionResource";

export type MealType = "breakfast" | "lunch" | "dinner" | "snack" | "pre_workout" | "post_workout";

export interface NutritionLog {
  id: string;
  user_id: string;
  date: string;
  created_at: string;
  meal_type: MealType;
  description: string;
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

export interface HydrationData {
  cups_consumed: number;
  cups_goal: number;
}

export interface NutritionGoals {
  calories_goal: number;
  protein_goal_g: number;
  carbs_goal_g: number;
  fat_goal_g: number;
}

export interface DailyTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

export interface WeeklyDay {
  date: string;
  calories: number;
  goal: number;
}

// ── Backward-compat re-exports used by Nutrition.tsx ─────────────────────────

export type { DailyTotals as Macros };

export interface DaySummary {
  date: string;
  pct: number;
}


const DEFAULT_GOALS: NutritionGoals = { calories_goal: 2000, protein_goal_g: 150, carbs_goal_g: 200, fat_goal_g: 65 };
export function nutritionDate(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function daysBack(count: number): string[] {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => nutritionDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() - count + 1 + i)));
}
async function readLogs(uid: string, since: string, until: string, assertCurrent: () => void): Promise<NutritionLog[]> {
  const rows: NutritionLog[] = [];
  let cursor: NutritionLog | undefined;
  while (true) {
    let query = supabase.from("nutrition_logs").select("*").eq("user_id", uid)
      .gte("date", since).lte("date", until).order("date", { ascending: false })
      .order("created_at", { ascending: false }).order("id", { ascending: false }).limit(500);
    if (cursor) {
      const date = JSON.stringify(cursor.date), created = JSON.stringify(cursor.created_at), id = JSON.stringify(cursor.id);
      query = query.or(`date.lt.${date},and(date.eq.${date},created_at.lt.${created}),and(date.eq.${date},created_at.eq.${created},id.lt.${id})`);
    }
    const { data, error } = await query;
    assertCurrent(); if (error) throw error;
    rows.push(...(data ?? []) as NutritionLog[]);
    if (!data || data.length < 500) return rows;
    cursor = data[data.length - 1] as NutritionLog;
  }
}
async function loadNutrition(uid: string, assertCurrent: () => void) {
  const days = daysBack(7);
  const [logs, hydrationResult, goalsResult] = await Promise.all([
    readLogs(uid, days[0], days[6], assertCurrent),
    supabase.from("nutrition_hydration").select("cups_consumed,cups_goal").eq("user_id", uid).eq("date", days[6]).maybeSingle(),
    supabase.from("nutrition_goals").select("calories_goal,protein_goal_g,carbs_goal_g,fat_goal_g").eq("user_id", uid).maybeSingle(),
  ]);
  assertCurrent(); if (hydrationResult.error) throw hydrationResult.error; if (goalsResult.error) throw goalsResult.error;
  const goals = (goalsResult.data ?? DEFAULT_GOALS) as NutritionGoals;
  return { logs: logs.filter(row => row.date === days[6]),
    hydration: (hydrationResult.data ?? { cups_consumed: 0, cups_goal: 8 }) as HydrationData,
    goals, hasCustomGoals: !!goalsResult.data,
    weeklyData: days.map(date => ({ date, goal: goals.calories_goal,
      calories: logs.filter(row => row.date === date).reduce((sum, row) => sum + Number(row.kcal), 0) })),
  };
}
export interface MealInput {
  meal_type: MealType; description?: string; name?: string;
  kcal?: number; calories?: number; protein?: number; protein_g?: number;
  carbs?: number; carbs_g?: number; fat?: number; fat_g?: number;
  fiber_g?: number; notes?: string; source?: string; confidence?: number;
  ingredients_detected?: string[] | null; client_action_id?: string;
}
function finiteNonnegative(values: number[]) {
  if (values.some(value => !Number.isFinite(value) || value < 0)) throw new Error("Invalid nutrition value");
}
export function nutritionActionId() {
  return "nutrition-" + Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
}

export function useNutrition() {
  const resource = useNutritionResource(loadNutrition);
  const { addXP } = useXP();
  const busy = useRef<symbol | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const pendingMeal = useRef<{ signature: string; id: string } | null>(null);
  useEffect(() => { busy.current = null; pendingMeal.current = null; setIsSaving(false); }, [resource.userId]);
  const logs = resource.data?.logs ?? [];
  const goals = resource.data?.goals ?? DEFAULT_GOALS;
  const hydration = resource.data?.hydration ?? { cups_consumed: 0, cups_goal: 8 };
  const weeklyData = resource.data?.weeklyData ?? [];
  const identity = useCallback(async () => {
    const current = resource.capture();
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user || user.id !== resource.userId || !current()) throw error ?? new Error("Nutrition session changed");
    return { uid: user.id, assertCurrent: () => { if (!current()) throw new Error("Nutrition session changed"); } };
  }, [resource.capture, resource.userId]);
  const mutate = useCallback(async <T,>(work: (uid: string, assertCurrent: () => void) => Promise<T>): Promise<T> => {
    if (busy.current) throw new Error("Nutrition save already in progress");
    const operation = Symbol(); busy.current = operation; setIsSaving(true);
    try {
      const { uid, assertCurrent } = await identity();
      const result = await work(uid, assertCurrent); assertCurrent();
      // A failed refresh cannot turn a confirmed write into a failed save.
      await resource.refresh(); assertCurrent(); notifyNutritionChanged(); return result;
    } finally { if (busy.current === operation) { busy.current = null; setIsSaving(false); } }
  }, [identity, resource.refresh]);

  const logMeal = useCallback((data: MealInput) => mutate(async (uid, assertCurrent) => {
    const description = (data.description ?? data.name ?? "").trim();
    const values = [data.kcal ?? data.calories ?? 0, data.protein ?? data.protein_g ?? 0, data.carbs ?? data.carbs_g ?? 0, data.fat ?? data.fat_g ?? 0];
    finiteNonnegative(values);
    if (!description || !["breakfast","lunch","dinner","snack","pre_workout","post_workout"].includes(data.meal_type)) throw new Error("Invalid meal");
    const row = { user_id: uid, date: nutritionDate(), meal_type: data.meal_type, description,
      kcal: Math.round(values[0]), protein: Math.round(values[1]), carbs: Math.round(values[2]), fat: Math.round(values[3]),
      source: ["manual","photo","voice","barcode"].includes(data.source ?? "") ? data.source : "manual",
      ...(typeof data.confidence === "number" ? { confidence: data.confidence } : {}),
      ...(Array.isArray(data.ingredients_detected) ? { ingredients_detected: data.ingredients_detected } : {}),
    };
    const signature = JSON.stringify(row);
    if (!pendingMeal.current || pendingMeal.current.signature !== signature) pendingMeal.current = { signature, id: nutritionActionId() };
    const client_action_id = data.client_action_id || pendingMeal.current.id;
    const { data: saved, error } = await supabase.from("nutrition_logs").insert({ ...row, client_action_id }).select("id,user_id,client_action_id").single();
    assertCurrent();
    let confirmed = saved; let duplicate = false;
    if (error?.code === "23505") {
      const result = await supabase.from("nutrition_logs").select("id,user_id,client_action_id,date,meal_type,description,kcal,protein,carbs,fat,source,confidence,ingredients_detected").eq("user_id",uid).eq("client_action_id",client_action_id).single();
      assertCurrent(); if (result.error) throw result.error;
      const existing = result.data;
      if (!existing || Object.entries(row).some(([key, value]) => JSON.stringify((existing as Record<string, unknown>)[key] ?? null) !== JSON.stringify(value ?? null))) throw new Error("Meal review conflicts with saved action");
      confirmed = existing; duplicate = true;
    } else if (error) throw error;
    if (!confirmed?.id || confirmed.user_id !== uid || confirmed.client_action_id !== client_action_id) throw new Error("Meal save not confirmed");
    pendingMeal.current = null;
    let xpAwarded = false;
    if (!duplicate) {
      try { const xp = await addXP("nutrition", "meal_logged", 15, confirmed.id, { ownerId: uid, isCurrent: () => { try { assertCurrent(); return true; } catch { return false; } } }); xpAwarded = !!xp?.inserted; }
      catch (error) { console.error("[Nutrition] XP failed after confirmed save", error); }
    }
    return { id: confirmed.id, xpAwarded, duplicate };
  }), [mutate, addXP]);

  const deleteLog = useCallback((id: string) => mutate(async (uid, assertCurrent) => {
    const { data, error } = await supabase.from("nutrition_logs").delete().eq("id",id).eq("user_id",uid).select("id");
    assertCurrent(); if (error) throw error;
    if (data?.length !== 1 || data[0].id !== id) throw new Error("Meal deletion not confirmed");
  }), [mutate]);
  const updateLog = useCallback((id: string, data: { description?: string; kcal?: number; protein?: number; carbs?: number; fat?: number; meal_type?: string }) => mutate(async (uid, assertCurrent) => {
    finiteNonnegative([data.kcal, data.protein, data.carbs, data.fat].filter(value => value !== undefined) as number[]);
    const { data: saved, error } = await supabase.from("nutrition_logs").update(data).eq("id",id).eq("user_id",uid).select("id");
    assertCurrent(); if (error) throw error;
    if (saved?.length !== 1 || saved[0].id !== id) throw new Error("Meal update not confirmed");
  }), [mutate]);
  const updateHydration = useCallback((newCups: number) => mutate(async (uid, assertCurrent) => {
    if (!Number.isInteger(newCups) || newCups < 0) throw new Error("Invalid hydration");
    const date = nutritionDate();
    const { data, error } = await supabase.from("nutrition_hydration").upsert({ user_id: uid, date, cups_consumed: newCups, cups_goal: hydration.cups_goal }, { onConflict: "user_id,date" }).select("user_id,date,cups_consumed").single();
    assertCurrent(); if (error) throw error;
    if (data?.user_id !== uid || data.date !== date || data.cups_consumed !== newCups) throw new Error("Hydration save not confirmed");
  }), [mutate, hydration.cups_goal]);
  const addWater = useCallback(async () => {
    if (hydration.cups_consumed >= hydration.cups_goal) return;
    await updateHydration(hydration.cups_consumed + 1);
  }, [hydration.cups_consumed, hydration.cups_goal, updateHydration]);
  const updateGoals = useCallback((newGoals: NutritionGoals) => mutate(async (uid, assertCurrent) => {
    await saveManualNutritionGoals(uid, newGoals, assertCurrent); assertCurrent();
  }), [mutate]);
  const getLogsHistory = useCallback(async (days: number) => {
    const { uid, assertCurrent } = await identity();
    const range = daysBack(days);
    return readLogs(uid, range[0], range[range.length - 1], assertCurrent);
  }, [identity]);
  const totals: DailyTotals = useMemo(() => logs.reduce((sum, row) => ({ calories: sum.calories + Number(row.kcal), protein: sum.protein + Number(row.protein), carbs: sum.carbs + Number(row.carbs), fat: sum.fat + Number(row.fat), fiber: 0 }), { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }), [logs]);
  const todayMeals = logs.map(row => ({ ...row, kcal: Number(row.kcal), protein: Number(row.protein), carbs: Number(row.carbs), fat: Number(row.fat) }));
  const targets = { kcal: goals.calories_goal, protein: goals.protein_goal_g, carbs: goals.carbs_goal_g, fat: goals.fat_goal_g };
  return { logs, weeklyData, hydration, goals, totals, targets, todayMeals,
    todayMacros: { kcal: totals.calories, protein: totals.protein, carbs: totals.carbs, fat: totals.fat },
    cups: hydration.cups_consumed, weekData: weeklyData.map(day => ({ date: day.date, pct: day.goal > 0 ? Math.round(day.calories / day.goal * 100) : 0 })),
    loading: resource.status === "loading", isLoading: resource.status === "loading",
    error: resource.status === "error" ? "nutrition.error" : null,
    status: resource.status, userId: resource.userId, checkedAt: resource.checkedAt, isSaving,
    hasCustomGoals: resource.data?.hasCustomGoals ?? false,
    refreshAll: resource.refresh, reload: resource.refresh,
    logMeal, deleteLog, updateLog, updateHydration, addWater, updateGoals, getLogsHistory,
  };
}
